import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
} from "@devhop/ui";
import {
  BarChart3Icon,
  CrownIcon,
  DownloadIcon,
  EyeIcon,
  EyeOffIcon,
  TrophyIcon,
  UsersIcon,
} from "lucide-react";
import { useMemo } from "react";
import type {
  AudienceVoteContestantDTO,
  AudienceVoteEventPhase,
} from "../api/client";
import { AudienceVoteContestantArtworkCell } from "./AudienceVoteContestantArtworkCell";

type AudienceVoteResultsTabProps = {
  eventId: string;
  phase: AudienceVoteEventPhase;
  showResults: boolean;
  uniqueVoters: number;
  totalVotes: number;
  contestants: AudienceVoteContestantDTO[];
  canExport: boolean;
  onExportCsv: () => void;
};

function RankMark({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-amber-100 font-bold text-amber-900 tabular-nums dark:bg-amber-950/80 dark:text-amber-100">
        <CrownIcon className="size-4" aria-hidden />
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-slate-200 font-semibold text-slate-800 tabular-nums dark:bg-slate-700 dark:text-slate-100">
        2
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-orange-100 font-semibold text-orange-900 tabular-nums dark:bg-orange-950/70 dark:text-orange-100">
        3
      </span>
    );
  }
  return (
    <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted font-medium text-muted-foreground tabular-nums">
      {rank}
    </span>
  );
}

function StatTile({
  label,
  value,
  hint,
  icon: Icon,
}: {
  label: string;
  value: number | string;
  hint?: string;
  icon: typeof UsersIcon;
}) {
  return (
    <Card className="overflow-hidden border-muted/80 shadow-sm">
      <CardContent className="flex items-start gap-4 px-5 py-0">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-muted-foreground text-sm">{label}</p>
          <p className="font-semibold text-3xl tabular-nums tracking-tight">
            {value}
          </p>
          {hint ? (
            <p className="mt-0.5 text-muted-foreground text-xs">{hint}</p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

export function AudienceVoteResultsTab({
  eventId,
  phase,
  showResults,
  uniqueVoters,
  totalVotes,
  contestants,
  canExport,
  onExportCsv,
}: AudienceVoteResultsTabProps) {
  const ranked = useMemo(() => {
    const list = [...contestants];
    list.sort((a, b) => {
      const vc = (b.voteCount ?? 0) - (a.voteCount ?? 0);
      if (vc !== 0) return vc;
      return a.sortOrder - b.sortOrder;
    });
    return list;
  }, [contestants]);

  const maxVotes = ranked[0]?.voteCount ?? 0;
  const avgPerVoter =
    uniqueVoters > 0
      ? (Math.round((totalVotes / uniqueVoters) * 10) / 10).toFixed(1)
      : "—";

  const topThree = ranked.slice(0, 3);

  return (
    <div className="mt-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <h2 className="font-semibold text-lg tracking-tight">
            ຜົນໂຫວດ & ອັນດັບ
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">phase: {phase}</Badge>
            <Badge
              variant={showResults ? "default" : "secondary"}
              className="gap-1"
            >
              {showResults ? (
                <EyeIcon className="size-3.5" />
              ) : (
                <EyeOffIcon className="size-3.5" />
              )}
              LIHF {showResults ? "ເຜີຍແຜ່ຜົນ" : "ຊ້ອນຜົນ"}
            </Badge>
          </div>
          <p className="max-w-xl text-muted-foreground text-sm">
            ສະຖິຕິ admin ເຫັນຄະແນນຕະຫຼອດ · public LIHF ຕາມ showResults + ຊ່ວງເວລາ
          </p>
        </div>
        {canExport ? (
          <Button variant="outline" onClick={onExportCsv}>
            <DownloadIcon className="mr-2 size-4" />
            Export CSV
          </Button>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <StatTile
          icon={UsersIcon}
          label="ຜູ້ໂຫວດ (unique)"
          value={uniqueVoters}
        />
        <StatTile icon={TrophyIcon} label="ຄະແນນທັງໝົດ" value={totalVotes} />
        <StatTile
          icon={BarChart3Icon}
          label="ຄ່າສະເລ່ຍ / ຜູ້ໂຫວດ"
          value={avgPerVoter}
          hint="total ÷ unique"
        />
      </div>

      {topThree.length > 0 && totalVotes > 0 ? (
        <div className="grid gap-3 md:grid-cols-3">
          {topThree.map((c, i) => {
            const rank = i + 1;
            const votes = c.voteCount ?? 0;
            const share =
              totalVotes > 0 ? Math.round((votes / totalVotes) * 1000) / 10 : 0;
            return (
              <Card
                key={c.id}
                className={cn(
                  "relative overflow-hidden border-muted/80 shadow-sm",
                  rank === 1 &&
                  "ring-1 ring-amber-500/30 md:-mt-1 md:scale-[1.02]",
                )}
              >
                {rank === 1 ? (
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500" />
                ) : null}
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <RankMark rank={rank} />
                    <div className="min-w-0 flex-1">
                      <CardTitle className="truncate text-base">
                        {c.displayName}
                      </CardTitle>
                      {c.panelLabel ? (
                        <CardDescription>Panel {c.panelLabel}</CardDescription>
                      ) : null}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  <div className="flex items-end justify-between gap-2">
                    <span className="font-semibold text-3xl tabular-nums">
                      {votes}
                    </span>
                    <span className="text-muted-foreground text-sm tabular-nums">
                      {share}% ຂອງທັງໝົດ
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all",
                        rank === 1
                          ? "bg-amber-500"
                          : rank === 2
                            ? "bg-slate-400"
                            : "bg-orange-400",
                      )}
                      style={{
                        width: `${maxVotes > 0 ? (votes / maxVotes) * 100 : 0}%`,
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : null}

      <Card className="border-muted/80 shadow-sm">
        <CardHeader className="border-b bg-muted/20">
          <CardTitle className="text-base">ຕາຕະລາງອັນດັບ</CardTitle>
          <CardDescription>
            {ranked.length} ນັກແຂ່ງ · ຈັດອັນດັບຕາມຄະແນນ
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {ranked.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground text-sm">
              ຍັງບໍ່ມີນັກແຂ່ງ
            </p>
          ) : (
            <ul className="divide-y">
              {ranked.map((c, i) => {
                const rank = i + 1;
                const votes = c.voteCount ?? 0;
                const barPct =
                  maxVotes > 0 ? Math.round((votes / maxVotes) * 100) : 0;
                return (
                  <li
                    key={c.id}
                    className={cn(
                      "flex flex-col gap-3 p-4 sm:flex-row sm:items-center",
                      rank <= 3 && "bg-muted/15",
                    )}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <RankMark rank={rank} />
                      <AudienceVoteContestantArtworkCell
                        eventId={eventId}
                        contestant={c}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{c.displayName}</p>
                        <p className="truncate text-muted-foreground text-xs">
                          {[c.panelLabel, c.subtitle]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </p>
                      </div>
                    </div>
                    <div className="flex w-full shrink-0 flex-col gap-1.5 sm:w-56">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-lg tabular-nums">
                          {votes}
                        </span>
                        <span className="text-muted-foreground text-xs tabular-nums">
                          {barPct}%
                        </span>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary/80"
                          style={{ width: `${barPct}%` }}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
